import { useEffect } from 'react';
import { IconClose } from './Icon';

interface KeyboardShortcutsModalProps {
  onClose: () => void;
}

interface Shortcut {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  title: string;
  items: Shortcut[];
}

// Detect whether ⌘ should be the modifier glyph or Ctrl. The actual key
// handlers throughout the app already bind both `metaKey` and `ctrlKey`
// for any "primary" shortcut so the rendering choice here is cosmetic.
function primaryModifier(): string {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return navigator.platform.includes('Mac') ? '⌘' : 'Ctrl';
}

export function KeyboardShortcutsModal({ onClose }: KeyboardShortcutsModalProps) {
  const mod = primaryModifier();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groups: ShortcutGroup[] = [
    {
      title: 'Navigation',
      items: [
        { keys: [mod, 'K'], description: 'Search messages and people' },
        { keys: [mod, '/'], description: 'Show this cheat sheet' },
        { keys: ['Esc'], description: 'Close the open modal or panel' },
      ],
    },
    {
      title: 'Composer',
      items: [
        {
          keys: ['Enter'],
          description:
            'Send the message (or insert a newline if "Press Enter to send" is off)',
        },
        {
          keys: ['Shift', 'Enter'],
          description: 'Insert a newline without sending',
        },
        {
          keys: [mod, 'Enter'],
          description: 'Send the message when "Press Enter to send" is off',
        },
        {
          keys: ['@'],
          description: 'Start typing to bring up the @-mention picker',
        },
        {
          keys: ['↑', '↓'],
          description: 'Navigate the @-mention picker',
        },
        {
          keys: ['Tab'],
          description: 'Confirm the highlighted @-mention',
        },
      ],
    },
    {
      title: 'Media viewer',
      items: [
        { keys: ['→'], description: 'Next image / video' },
        { keys: ['←'], description: 'Previous image / video' },
        { keys: ['Esc'], description: 'Close the lightbox' },
      ],
    },
  ];

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl max-h-[85vh] flex flex-col rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl"
      >
        <header className="px-6 py-4 hairline flex items-center justify-between shrink-0">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-0.5">
              Cheat sheet
            </p>
            <h2 className="text-base font-semibold tracking-tight">
              Keyboard shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-xl text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition flex items-center justify-center"
          >
            <IconClose size={13} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {groups.map((group) => (
            <section key={group.title}>
              <h3 className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">
                {group.title}
              </h3>
              <ul className="rounded-md border border-zinc-800 bg-zinc-900/30 divide-y divide-zinc-900">
                {group.items.map((s, i) => (
                  <li
                    key={i}
                    className="px-3 py-2 flex items-center justify-between gap-3"
                  >
                    <p className="text-sm text-zinc-300">{s.description}</p>
                    <div className="shrink-0 flex items-center gap-1">
                      {s.keys.map((k, ki) => (
                        <kbd
                          key={ki}
                          className="text-[10px] font-mono text-zinc-300 bg-zinc-950 hairline px-1.5 py-0.5 rounded-md min-w-[1.5rem] text-center"
                        >
                          {k}
                        </kbd>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
